import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { useAppDispatch } from "hooks";
import Notice from "components/tw/notice";
import { btnPrimaryCls, btnSecondaryCls } from "components/tw/classes";
import NewRequestDialog from "features/asset-requests/NewRequestDialog";
import SelfAssetRequests from "features/asset-requests/SelfAssetRequests";
import { fetchCabinetSummaryThunk } from "features/cabinet-tasks/cabinet-summary.slice";
import { QueueSkeleton } from "features/cabinet-queues/queue-list-states";
import { useQueueData } from "features/cabinet-queues/use-queue-data";
import { Translate } from "features/cabinet-tasks/task-row";
import { fetchMyAssets } from "services/cabinet-me.service";
import AssetCard from "./asset-card";

/** What the line over the cards says: how many items, and how many acts wait for the person's signature. */
function statusLine(itemCount: number, actsToSign: number, t: Translate): string {
  if (itemCount === 0) return t("cabinet_assets_none");
  const held = t("cabinet_assets_count", { count: itemCount });
  if (actsToSign === 0) return held;
  return `${held}, ${t("cabinet_assets_acts_to_sign", { count: actsToSign })}`;
}

/**
 * «Turtas» in the cabinet: a line saying what the person holds, one main button for an asset
 * request, a card for every item and the list of the person's earlier requests below.
 */
export default function CabinetAssets() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId = "", userId = "" } = useParams();
  const assets = useQueueData(() => fetchMyAssets(companyId), { scopeKey: companyId });
  const [isAsking, setIsAsking] = useState(false);
  // «Mano prašymai» loads again when a request has just been filed.
  const [requestsReloadCount, setRequestsReloadCount] = useState(0);

  const items = assets.data ?? [];
  const actsToSign = items.filter((asset) => asset.act.status === "to_sign").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        {assets.data && (
          <p className={`text-base font-semibold ${actsToSign > 0 ? "text-amber-700" : "text-gray-900"}`}>
            {statusLine(items.length, actsToSign, t)}
          </p>
        )}
        <div className="flex md:ml-auto">
          <button type="button" className={`${btnPrimaryCls} w-full md:w-auto`} onClick={() => setIsAsking(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            {t("asset_requests_self_button")}
          </button>
        </div>
      </div>

      {assets.hasFailed && (
        <Notice tone="critical" title={t("cabinet_assets_load_failed")}>
          <button type="button" className={btnSecondaryCls} onClick={assets.reload}>
            {t("try_again")}
          </button>
        </Notice>
      )}
      {!assets.data && assets.isLoading && <QueueSkeleton />}

      {items.length > 0 && (
        <section>
          {/* Only for screen readers: the card titles are h3, so they need an h2 above them. */}
          <h2 className="sr-only">{t("cabinet_assets_cards_title")}</h2>
          <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {items.map((asset) => (
              <AssetCard key={asset.id} asset={asset} />
            ))}
          </ul>
        </section>
      )}

      <SelfAssetRequests companyId={companyId} reloadCount={requestsReloadCount} />

      {isAsking && (
        <NewRequestDialog
          companyId={companyId}
          selfUserId={userId}
          onClose={() => setIsAsking(false)}
          onCreated={() => {
            setIsAsking(false);
            setRequestsReloadCount((count) => count + 1);
            // An issuer asking for themselves also sees the queue: keep the Turtas badge in step.
            dispatch(fetchCabinetSummaryThunk(companyId));
          }}
        />
      )}
    </div>
  );
}
